/**
 * ReceiptOverlay — the one receipt viewer across Vault (Pass 1 shared
 * component). Opens any stored receipt: the image (or a link for a PDF), its
 * merchant / total / date and what it is attached to. Receipts live behind
 * auth — nginx denies /media/, so the caller passes `src` (an /api receipt
 * action path) and the bytes are pulled with the JWT into a blob shown from an
 * object URL. "✨ Re-read with AI" re-runs OCR on the stored image (onReread)
 * into the editable Total field with a plain-English read-back and Undo; "Apply
 * total" (onApplyTotal) sets the expense's spent — reversible like any entry.
 * Every target is >=44px.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { blobUrl } from '../api.js';
import { fmtR } from '../money.js';
import { AskEnableAI, useAI } from './AIGate.jsx';

export default function ReceiptOverlay({
  open, onClose, src, merchant, total, date, attachedTo,
  onReread, onApplyTotal, onDetach,
}) {
  const ai = useAI();   // AI master switch — the re-read button hides or disables while off
  const [busy, setBusy] = useState('');
  const [img, setImg] = useState(null);        // { url, type } object URL, or null
  const [state, setState] = useState('idle');  // idle | loading | ready | error
  const [rt, setRt] = useState('');            // editable Total (re-read fills this)
  const [rm, setRm] = useState('');            // merchant (display, re-read may update)
  const [rd, setRd] = useState('');            // date (display, re-read may update)
  const [line, setLine] = useState('');        // plain-English read-back
  const before = useRef(null);                 // Total before the last AI fill (Undo)

  // Pull the receipt bytes through the authenticated action on open.
  useEffect(() => {
    if (!open || !src) { setImg(null); setState('idle'); return; }
    let alive = true; let made = null;
    setImg(null); setState('loading');
    blobUrl(src)
      .then((r) => { if (!alive) { URL.revokeObjectURL(r.url); return; } made = r.url; setImg(r); setState('ready'); })
      .catch(() => { if (alive) setState('error'); });
    return () => { alive = false; if (made) URL.revokeObjectURL(made); };
  }, [open, src]);

  // Seed the editable/display fields from props each time it opens.
  useEffect(() => {
    if (!open) return;
    setRt(total != null && total !== '' ? String(total) : '');
    setRm(merchant || ''); setRd(date || ''); setLine(''); before.current = null;
  }, [open, total, merchant, date]);

  if (!open) return null;

  const isPdf = img && /pdf/i.test(img.type || '');

  const doReread = async () => {
    if (!onReread || busy) return;
    setBusy('reread'); setLine('');
    try {
      const d = await onReread();               // { merchant, total, date } | null
      if (d && (d.total != null || d.merchant || d.date)) {
        before.current = rt;
        if (d.total != null) setRt(String(d.total));
        if (d.merchant) setRm(d.merchant);
        if (d.date) setRd(d.date);
        const bits = [d.merchant, d.total != null ? fmtR(d.total) : null, d.date].filter(Boolean);
        setLine(`Read ${bits.join(' · ') || 'the receipt'} — edit the Total, then Apply.`);
      } else {
        setLine('Could not read the receipt — enter the total by hand.');
      }
    } catch (e) {
      setLine(e?.data?.detail || 'Re-read failed — enter the total by hand.');
    } finally { setBusy(''); }
  };

  const undo = () => { if (before.current != null) setRt(before.current); before.current = null; setLine('Reverted.'); };

  const doApply = async () => {
    if (!onApplyTotal || busy) return;
    const v = Number(String(rt).replace(',', '.'));
    if (!(v >= 0)) { setLine('Enter a valid total first.'); return; }
    setBusy('apply');
    try { await onApplyTotal(v); setLine(`Logged ${fmtR(v)} — it’s under Logged now, and reversible.`); before.current = null; }
    catch (e) { setLine(e?.data?.detail || 'Could not apply that total.'); }
    finally { setBusy(''); }
  };

  const doDetach = async () => {
    if (!onDetach || busy) return;
    setBusy('detach');
    try { await onDetach(); onClose?.(); } finally { setBusy(''); }
  };

  const rowLabel = 'text-slate-500 w-24 shrink-0';
  return createPortal((
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/75 backdrop-blur-[2px]" />
      <div className="relative bg-panel border border-edge rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 p-4 border-b border-edge">
          <h3 className="font-bold flex-1">🧾 Receipt</h3>
          <button className="w-11 h-11 rounded-full border border-edge text-slate-400 hover:text-white grid place-items-center shrink-0"
                  onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="p-4 space-y-3">
          {!src
            ? <div className="rounded-xl border border-edge px-4 py-8 text-center text-sm text-slate-500">No image on file</div>
            : state === 'loading'
              ? <div className="grid place-items-center rounded-xl border border-edge px-4 py-10 text-sm text-slate-500 bg-ink min-h-[44px]">Loading receipt…</div>
              : state === 'error'
                ? <div className="grid place-items-center rounded-xl border border-red-400/40 px-4 py-8 text-center text-sm text-red-400">Couldn’t load this receipt — it may have been removed.</div>
                : img && (isPdf
                  ? <a href={img.url} target="_blank" rel="noreferrer"
                       className="grid place-items-center rounded-xl border border-edge px-4 py-8 text-sm text-vblue hover:border-vblue min-h-[44px]">
                      📄 Open the PDF receipt ▸
                    </a>
                  : <img src={img.url} alt="Receipt" className="w-full rounded-xl border border-edge object-contain max-h-[46vh] bg-ink" />)}

          <div className="grid gap-2 text-sm">
            {(rm || merchant) && <div className="flex gap-2"><span className={rowLabel}>Merchant</span><span className="flex-1 font-medium min-w-0">{rm || merchant}</span></div>}
            {onApplyTotal ? (
              <div className="flex items-center gap-2">
                <span className={rowLabel}>Total</span>
                <input className="input flex-1 !py-1.5 text-sm" inputMode="decimal" placeholder="e.g. 436,86"
                       value={rt} onChange={(e) => setRt(e.target.value)} />
              </div>
            ) : ((total != null && total !== '') && <div className="flex gap-2"><span className={rowLabel}>Total</span><span className="flex-1 font-medium">{fmtR(total)}</span></div>)}
            {(rd || date) && <div className="flex gap-2"><span className={rowLabel}>Date</span><span className="flex-1">{rd || date}</span></div>}
            {attachedTo && <div className="flex gap-2"><span className={rowLabel}>Attached to</span><span className="flex-1 min-w-0 truncate">{attachedTo}</span></div>}
          </div>

          {line && (
            <p className="text-xs flex items-center gap-2" style={{ color: 'var(--color-accent-400)' }}>
              <span className="min-w-0">{line}</span>
              {before.current != null && (
                <button type="button" onClick={undo} className="underline shrink-0" style={{ color: 'var(--color-text)' }}>Undo</button>
              )}
            </p>
          )}

          <div className="flex gap-2 pt-1 flex-wrap">
            {onReread && ai.disabled && (
              <span className="flex-1 flex flex-col items-center justify-center gap-1">
                <button className="rounded-xl border px-3 py-2 text-sm min-h-[44px] w-full opacity-40 cursor-not-allowed"
                        style={{ borderColor: 'var(--color-divider)' }} disabled>✨ Re-read with AI</button>
                <AskEnableAI feature="receipt re-read" />
              </span>
            )}
            {onReread && ai.on && (
              <button className="rounded-xl border px-3 py-2 text-sm min-h-[44px] flex-1 disabled:opacity-50"
                      style={{ borderColor: 'var(--color-accent)', color: 'var(--color-accent)' }}
                      disabled={!!busy || state !== 'ready'} onClick={doReread}>
                {busy === 'reread' ? 'Reading…' : '✨ Re-read with AI'}
              </button>
            )}
            {onApplyTotal && (
              <button className="btn-primary !py-2 text-sm min-h-[44px] flex-1 disabled:opacity-50"
                      disabled={!!busy || !String(rt).trim()} onClick={doApply}>
                {busy === 'apply' ? 'Applying…' : 'Apply total'}
              </button>
            )}
            {onDetach && (
              <button className="rounded-xl border border-red-400/50 text-red-400 py-2 px-3 text-sm hover:bg-red-400/10 transition min-h-[44px]"
                      disabled={!!busy} onClick={doDetach}>
                {busy === 'detach' ? 'Detaching…' : 'Detach'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  ), document.body);
}
